import { defineStore } from 'pinia';
import SipVoiceClient from '../api/channel/voice/sipVoiceClient';
import VoiceCallsAPI from '../api/channel/voice/callsAPI';

// GK-HUB: a operadora infotec exige discagem 0+DDD+numero. SIP.js manda o que
// o agente digitou — então normalizamos aqui antes do INVITE pra evitar
// 603 Declined/Not Found dependendo do formato. Ramais (≤6 dígitos) e
// service codes (*99, #100) passam sem alteração.
function normalizeForDial(raw) {
  const target = String(raw || '').trim();
  if (!target) return target;

  // Service codes / extensões curtas / qualquer coisa com não-dígito relevante (*, #)
  if (/^[*#]/.test(target)) return target;

  const digits = target.replace(/\D/g, '');
  if (digits.length <= 6) return digits;

  // E.164 BR (+55 + 11 ou 10 dígitos)
  if (digits.length === 13 && digits.startsWith('55')) {
    return `0${digits.slice(2)}`;
  }
  if (digits.length === 12 && digits.startsWith('55')) {
    return `0${digits.slice(2)}`;
  }

  // Nacional sem código de país (DDD + número, 10 ou 11 dígitos)
  if (digits.length === 11 || digits.length === 10) {
    return `0${digits}`;
  }

  // Já vem com 0 na frente (0DDD + número, 11 ou 12 dígitos)
  if (
    (digits.length === 12 || digits.length === 11) &&
    digits.startsWith('0')
  ) {
    return digits;
  }

  // Outros formatos (internacional não-BR, atalhos) — não mexe
  return digits;
}

let durationInterval = null;
let onIncoming = null;
let onEstablished = null;
let onDisconnected = null;

export const useVoiceStore = defineStore('voice', {
  state: () => ({
    registered: false,
    incomingCall: null,
    activeCall: null,
    outgoingCall: null,
    callDuration: 0,
    dialpadOpen: false,
    sipUsername: null,
    answering: false,
    calling: false,
    transferring: false,
    activeUniqueId: null,
    showTransferDialog: false,
    muted: false,
    held: false,
    callContext: null,
    callContextLoading: false,
  }),

  getters: {
    hasIncomingCall: state => !!state.incomingCall,
    hasActiveCall: state => !!state.activeCall,
    hasOutgoingCall: state => !!state.outgoingCall,
    isInCall: state => !!state.activeCall || !!state.outgoingCall,
    formattedDuration: state => {
      const mins = Math.floor(state.callDuration / 60);
      const secs = state.callDuration % 60;
      return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    },
  },

  actions: {
    handleIncomingCall({ callerNumber, invitation }) {
      this.incomingCall = { callerNumber, invitation };
    },

    handleCallEstablished({ target, direction = 'outbound' }) {
      this.outgoingCall = null;
      this.activeCall = {
        number: target || this.incomingCall?.callerNumber,
        direction,
        startedAt: Date.now(),
      };
      this.incomingCall = null;
      this.startDurationTimer();
    },

    handleCallDisconnected() {
      this.incomingCall = null;
      this.activeCall = null;
      this.outgoingCall = null;
      this.answering = false;
      this.calling = false;
      this.transferring = false;
      this.activeUniqueId = null;
      this.showTransferDialog = false;
      this.muted = false;
      this.held = false;
      this.callContext = null;
      this.stopDurationTimer();
    },

    // C3 fix: receives { event, data } shape from ActionCable handler
    handleVoiceEvent({ event, data }) {
      switch (event) {
        case 'voice.ringing':
        case 'voice.answered':
          if (data?.unique_id) {
            this.activeUniqueId = data.unique_id;
            this.fetchCallContext(data.unique_id);
          }
          break;
        case 'voice.missed':
          this.handleCallDisconnected();
          break;
        case 'voice.transferred':
          this.handleCallDisconnected();
          break;
        default:
          break;
      }
    },

    // GK-HUB: faz fetch do payload pro softphone exibir contact + custom_attrs
    // do IVR (status_financeiro, qtd_boletos_atrasados, valor_atraso, etc).
    async fetchCallContext(uniqueId) {
      if (!uniqueId) return;
      this.callContextLoading = true;
      try {
        const { data } = await VoiceCallsAPI.context(uniqueId);
        if (this.activeUniqueId === uniqueId) {
          this.callContext = data;
        }
      } catch {
        // silent — softphone funciona sem o contexto
      } finally {
        this.callContextLoading = false;
      }
    },

    async answerCall() {
      // I9 fix: guard against rapid double-clicks
      if (!this.incomingCall || this.answering) return;
      this.answering = true;
      const audioEl = document.getElementById('gk-sip-remote-audio');
      if (audioEl) {
        audioEl.play().catch(() => {});
      }
      try {
        await SipVoiceClient.answer();
        this.handleCallEstablished({
          target: this.incomingCall.callerNumber,
          direction: 'inbound',
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Voice] Failed to answer:', error);
        this.handleCallDisconnected();
      } finally {
        this.answering = false;
      }
    },

    rejectCall() {
      SipVoiceClient.hangup();
      this.incomingCall = null;
    },

    async makeCall(target) {
      // I9 fix: guard against rapid double-clicks
      if (this.isInCall || this.calling) return;
      this.calling = true;
      const dialed = normalizeForDial(target);
      this.outgoingCall = { target: dialed };
      try {
        await SipVoiceClient.call(dialed);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Voice] Failed to call:', error);
        this.outgoingCall = null;
      } finally {
        this.calling = false;
      }
    },

    endCall() {
      SipVoiceClient.hangup();
      this.handleCallDisconnected();
    },

    toggleMute() {
      this.muted = SipVoiceClient.toggleMute();
    },

    toggleHold() {
      this.held = SipVoiceClient.toggleHold();
    },

    sendDtmf(digit) {
      SipVoiceClient.sendDtmf(digit);
    },

    transferCall(target) {
      if (SipVoiceClient.transfer(target)) {
        this.handleCallDisconnected();
      }
    },

    toggleDialpad() {
      this.dialpadOpen = !this.dialpadOpen;
    },

    setActiveUniqueId(uniqueId) {
      this.activeUniqueId = uniqueId;
    },

    toggleTransferDialog() {
      this.showTransferDialog = !this.showTransferDialog;
    },

    async initializeSip(credentials) {
      // I10 fix: only store non-sensitive data
      this.sipUsername = credentials.username;
      // GK-HUB: mapeia campos do CredentialsController p/ o que SipVoiceClient.initialize() espera.
      // Backend retorna ws_url/secret/sip_uri/ice_servers; SIP.js espera server/password/domain/iceServers.
      const sipUriMatch = (credentials.sip_uri || '').match(/^sip:[^@]+@(.+)$/);
      const sipInitOpts = {
        username: credentials.username,
        password: credentials.password || credentials.secret,
        server: credentials.server || credentials.ws_url,
        domain:
          credentials.domain || (sipUriMatch ? sipUriMatch[1] : undefined),
        iceServers: credentials.iceServers || credentials.ice_servers,
      };
      try {
        await SipVoiceClient.initialize(sipInitOpts);
        this.registered = true;

        // I4 fix: save listener references for cleanup in destroySip
        onIncoming = e => this.handleIncomingCall(e.detail);
        onEstablished = e =>
          this.handleCallEstablished({
            target: e.detail.target,
            direction: 'outbound',
          });
        onDisconnected = () => this.handleCallDisconnected();

        SipVoiceClient.addEventListener('call:incoming', onIncoming);
        SipVoiceClient.addEventListener('call:established', onEstablished);
        SipVoiceClient.addEventListener('call:disconnected', onDisconnected);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('[Voice] SIP init failed:', error);
        this.registered = false;
      }
    },

    destroySip() {
      // I4 fix: remove event listeners before destroying
      if (onIncoming) {
        SipVoiceClient.removeEventListener('call:incoming', onIncoming);
      }
      if (onEstablished) {
        SipVoiceClient.removeEventListener('call:established', onEstablished);
      }
      if (onDisconnected) {
        SipVoiceClient.removeEventListener('call:disconnected', onDisconnected);
      }
      onIncoming = null;
      onEstablished = null;
      onDisconnected = null;
      SipVoiceClient.destroy();
      this.registered = false;
      this.handleCallDisconnected();
    },

    startDurationTimer() {
      this.callDuration = 0;
      durationInterval = setInterval(() => {
        this.callDuration += 1;
      }, 1000);
    },

    stopDurationTimer() {
      if (durationInterval) {
        clearInterval(durationInterval);
        durationInterval = null;
      }
      this.callDuration = 0;
    },
  },
});
